import { memo, useMemo, CSSProperties } from 'react'
import { List, Map } from 'immutable'

import useModes from '../../states/useModes'
import useComponentLayout from '../../states/useComponentLayout'
import useSelectedItems from '../../states/useSelectedItems'
import useSigtest from '../../states/useSigtest'
import useFitListToTable from '../../states/useFitListToTable'

import { DEFAULT_DATA_HEIGHT, ROW_PADDING } from '../../config/CompareItems_const'
import { CompareItemsTableItemCell as TCompareItemsTableItemCell } from '../../types'

import { decodeText } from '@aytm/app/stats/lib/util/helpers'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import InlineSelect from '@aytm/lib/rfd-ui/components/basic/inline-select'

import style from './ItemCell.module.scss'

export default memo(
  CompareItemsTableItemCell,
  (prevProps, nextProps) =>
    prevProps.width === nextProps.width &&
    prevProps.className === nextProps.className &&
    prevProps.cell === nextProps.cell
)
export function CompareItemsTableItemCell({
  className,
  width,
  cell
}: {
  className: string
  width: number
  cell: TCompareItemsTableItemCell
}) {
  const modes = useModes()
  const selectedItems = useSelectedItems()
  const componentLayout = useComponentLayout()
  const sigtest = useSigtest()

  const {
    dataVisualWidth = 0,
    attributeColFullWidth = 0,
    dataHeight = DEFAULT_DATA_HEIGHT,
    visualContentWidth = 0
  } = componentLayout.measures
  const list = useFitListToTable(dataHeight + ROW_PADDING, attributeColFullWidth)
  const labelStyle =
    0 <= dataVisualWidth && width > dataVisualWidth
      ? {
          position: 'sticky' as CSSProperties['position'],
          left: attributeColFullWidth + 6,
          width: 'max-content' as CSSProperties['width'],
          maxWidth: Math.min(width, dataVisualWidth - 12)
        }
      : null
  const swapButtonStyle: CSSProperties = cell.splitSwap
    ? {
        left: visualContentWidth - 26
      }
    : null

  const fetchItemOptions = useMemo(
    () =>
      cell.canChange
        ? (filterValue: string) =>
            List(
              cell.items
                .map(item =>
                  !filterValue || decodeText(item.text).toLowerCase().includes(filterValue.toLowerCase())
                    ? Map({
                        value: item.position,
                        filterValue: decodeText(item.text),
                        render: () => (
                          <>
                            <b className={style.selector__option__item_name}>
                              {cell.itemName} {item.position}.
                            </b>
                            <br />
                            {decodeText(item.text)}
                          </>
                        )
                      })
                    : null
                )
                .filter(item => Boolean(item))
            )
        : null,
    [cell.canChange, cell.items, cell.itemName]
  )

  const itemLabel = (
    <>
      <div className={style.CompareItemsTableItemCell__title} style={labelStyle}>
        {cell.itemName} {cell.item.position}.
      </div>
      <div className={style.CompareItemsTableItemCell__text} style={labelStyle}>
        {decodeText(cell.item.text)}
      </div>
      {'item' === sigtest?.type && sigtest?.letters.has(cell.item._index) ? (
        <div
          className={`${style.CompareItemsTableItemCell__sigtest} ${
            cell.splitSwap ? style['CompareItemsTableItemCell__sigtest--splitSwap'] : ''
          }`}
        >
          {sigtest.letters.get(cell.item._index)}
        </div>
      ) : null}
      {cell.splitSwap && modes.useSplitBy ? (
        <i
          className={`icon-aytmfd-swap ${style.CompareItemsTableItemCell__swap_button}`}
          title="Swap items and split values"
          style={swapButtonStyle}
          onClick={() =>
            void modes.onChangeSplitBy({
              ...modes.splitBy,
              group: 'items' === modes.splitBy.group ? 'values' : 'items'
            })
          }
        />
      ) : null}
    </>
  )

  return (
    <div
      className={`${className || ''} ${style.CompareItemsTableItemCell} ${
        modes.fullTexts ? '' : style['CompareItemsTableItemCell--fixed-height']
      } ${cell.canChange || sigtest ? '' : style['CompareItemsTableItemCell--full-width']} ${
        false !== cell.lastInGroup ? style['CompareItemsTableItemCell--lastInGroup'] : ''
      } ${cell.subGroup ? style['CompareItemsTableItemCell--subGroup'] : ''}`}
      style={{ flex: `0 0 ${width}px`, width: `${width}px` }}
      data-testid="stat-compare-items-item"
      data-compare-items="item-cell"
      data-compare-items-header-col={cell.col}
    >
      {cell.canChange ? (
        <InlineSelect
          innerRef={list.selectRef}
          onShow={list.onShow}
          fetchOptions={fetchItemOptions}
          onSelect={(option: Map<string, number | (() => JSX.Element)>) => {
            const itemPosition = option.get('value') as number
            const itemIndex = cell.items.findIndex(item => item.position === itemPosition)
            if (null != itemIndex) {
              selectedItems.changeItem(cell.selectedItemIndex, itemIndex)
            }
          }}
          className={style.selector}
          value={cell.item.position}
          maxHeight={list.maxHeight}
          filter={cell.items.length > 9}
        >
          {itemLabel}
        </InlineSelect>
      ) : (
        <div className={style.selector}>{itemLabel}</div>
      )}
      {selectedItems.onClickItem ? (
        <span
          className={style.CompareItemsTableItemCell__scroll_to_details}
          data-testid="stat-compare-items-scroll-to-details"
          {...pressable((): void => void selectedItems.onClickItem(cell.selectedItemIndex))}
        >
          Scroll to details
        </span>
      ) : null}
    </div>
  )
}
